import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { formatDate } from '../utils/helpers';
import { motion } from 'framer-motion';
import { ExternalLink, Star } from 'lucide-react';
import api from '../api/api';

export default function Confirmation() {
  const { orderDetails, setOrderDetails, user } = useApp();
  const navigate = useNavigate();
  const [googleReviewLink, setGoogleReviewLink] = useState('');

  useEffect(() => {
    if (!orderDetails) navigate('/home');
  }, [orderDetails, navigate]);

  useEffect(() => {
    const fetchGoogleReviewLink = async () => {
      try {
        const res = await api.GetContactUs('ADMIN');
        const contactInfo =
          res?.data?.GetContactUsjs?.Table1?.[0] ||
          res?.GetContactUsjs?.Table1?.[0] ||
          {};
        setGoogleReviewLink(String(contactInfo.GoogleReview || '').trim());
      } catch (err) {
        console.error('Google review link fetch failed:', err);
      }
    };

    fetchGoogleReviewLink();
  }, []);

  if (!orderDetails) return null;
console.log("order Details", orderDetails);
  const d = orderDetails;
  

  // Generate daily summary using window.__menuItemMap
  const dailySummary = Object.entries(d?.cart || {}).map(([date, itemsObj]) => {
    const itemList = Object.entries(itemsObj).map(([itemId, qty]) => {
      const item = window.__menuItemMap?.[itemId];
      if (!item) return null;
      return (
        <li
          key={itemId}
          className="flex justify-between py-1 text-sm border-b border-gray-100 sm:text-base"
        >
          <span>{item.name} x {qty}</span>
          <span>₹{(item.price * qty).toFixed(2)}</span>
        </li>
      );
    }).filter(Boolean);

    if (!itemList.length) return null;

    return (
      <div key={date} className="mb-3">
        <p className="font-semibold text-gray-700">{formatDate(date)}</p>
        <ul className="p-2 mt-1 rounded-lg bg-gray-50">{itemList}</ul>
      </div>
    );
  }).filter(Boolean);

  return (
    <div className="min-h-screen px-4 py-8 sm:px-6 lg:px-10">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-lg p-6 mx-auto bg-white shadow-xl rounded-3xl sm:p-10"
      >
        {/* Success Icon */}
        <div className="flex justify-center mb-4">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-16 h-16 text-green-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>

        {/* Header */}
        <h2 className="mb-2 text-2xl font-bold text-center text-gray-800 sm:text-3xl">
          Order Placed Successfully!
        </h2>
        <p className="mb-2 text-center text-gray-600">
  Thank you, {user?.Vname || d?.user?.name?.split?.(' ')[0] || 'Guest'}. Your meals are confirmed.
</p>

<div className="flex justify-center mb-2">
  <span
    className={`px-4 py-1 text-sm font-semibold rounded-full ${
      d?.isTakeaway
        ? "bg-teal-100 text-teal-700"
        : "bg-yellow-100 text-yellow-700"
    }`}
  >
    {d?.isTakeaway ? "🛍️ Takeaway" : "🛵 Delivery"}
  </span>
</div>

        {/* Order Summary */}
        <div className="p-4 space-y-4 border border-gray-200 bg-gray-50 rounded-xl">
          <div className="flex justify-between">
            <span className="font-semibold">Order ID:</span>
            <span>{d?.id || 'N/A'}</span>
          </div>
          <div className="flex justify-between">
            <span className="font-semibold">{d?.isTakeaway ? 'Pickup Address:' : 'Delivery Address:'}</span>
            <span>
              {d?.isTakeaway ? (d?.user?.companyAddress || 'N/A') : (d?.address || 'N/A')}
            </span>
          </div>

          <hr className="my-2" />

          <div>
            <h4 className="mb-2 font-semibold text-gray-700">Daily Order Details:</h4>
            {dailySummary.length ? dailySummary : <p className="text-gray-500">No items found.</p>}
          </div>

          <hr className="my-2" />

          <div className="space-y-1">
            <div className="flex justify-between">
              <span>Total Items Cost:</span>
              <span>₹{d?.totals?.subtotal?.toFixed?.(2) || '0.00'}</span>
            </div>
            {d?.totals?.deliveryCharge && (
              <div className="flex justify-between">
                <span>Delivery Fee:</span>
                <span>₹{d.totals.deliveryCharge.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between pt-2 text-lg font-bold border-t">
              <span>Grand Total:</span>
              <span>₹{d?.totals?.total?.toFixed?.(2) || '0.00'}</span>
            </div>
          </div>

          {/* Payment */}
          <div className="p-3 mt-4 rounded-lg bg-green-50">
            <p className="font-semibold text-green-800">
              Payment Method: {d?.paymentMethod === 'upi' ? 'Paid via UPI' : d?.paymentMethod === 'netbanking' ? 'Paid via Net Banking' : d?.paymentMethod === 'card' ? 'Paid via Card' : 'Cash on Delivery'}
            </p>
            {d?.referenceNo && (
              <p className="mt-1 text-sm text-green-700">Reference No: {d.referenceNo}</p>
            )}
          </div>
        </div>

        {googleReviewLink && (
          <a
            href={googleReviewLink}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-3 p-4 mt-5 text-left transition-all border border-yellow-200 shadow-sm bg-yellow-50 rounded-2xl hover:bg-yellow-100"
          >
            <span className="flex items-center justify-center flex-none w-11 h-11 text-yellow-600 bg-white rounded-full">
              <Star size={22} fill="currentColor" />
            </span>
            <span className="min-w-0">
              <span className="block font-bold text-gray-900">Rate us on Google and leave a review.</span>
              <span className="block mt-1 text-sm text-gray-600">Your support helps us grow!</span>
            </span>
            <ExternalLink size={18} className="flex-none ml-auto text-yellow-700" />
          </a>
        )}

        {/* Back Button */}
        <button
          onClick={() => {
            setOrderDetails(null);
            navigate('/home');
          }}
          className="w-full py-3 mt-6 font-semibold text-white transition-all bg-red-500 rounded-full shadow hover:bg-red-600"
        >
          ← Back to Home
        </button>
      </motion.div>
    </div>
  );
}
